import {getActiveTutorialContext} from '../../configs/tutorial';
import {checkIfActionGivesAdditionalSupply, checkTutorialActionAllowed} from '../../utils/tutorial/tutorialActionGate';
import {REGISTRY_SINGLETON} from '../../scenes/Registry';
import {
  checkIfAnyMeleeResultHeld,
  getLastMeleeResultReleaseAt,
  subscribeToMeleeResultHolds,
} from '../../utils/effects/meleeAnimationTracker';
import {checkTutorialPredicate} from '../../utils/tutorial/tutorialStepPredicates';
import {enqueueTutorialDamageDice} from '../../utils/tutorial/tutorialDice';
import {deselectUnit, showToast, tutorialAdvanceStep, tutorialChapterCompleted, tutorialSetModal} from '../actions';
import {GAME_TYPES} from '../../constants/game';
import {getTutorialReplayRecorder} from '../../utils/tutorial/replay/tutorialReplayRecorderRegistry';
import {buildTutorialBlockedActionRecord} from '../../utils/tutorial/replay/tutorialReplayRecordRules';
import {handleCompleteTurn} from '../../utils/gameState/turn/handleCompleteTurn';
import {selectCurrentPlayer} from '../reducers/sharedStateReducer/selectors';

// Тот же текст не чаще раза в полторы секунды: клики по полю сыплются часто,
// а тост в приложении одиночный (новый затирает старый) — без глушения
// предупреждение мигало бы
const BLOCKED_TOAST_THROTTLE_MS = 1500;
let lastBlockedToastAt = 0;

// Диспатчи самого движка сценария (advance/setModal) проходят через это же
// middleware — вложенные вызовы progressTutorial глушим, продвижение целиком
// управляется внешним циклом
let isProgressing = false;

const showBlockedActionToast = (store, step, action) => {
  const now = Date.now();
  if (now - lastBlockedToastAt < BLOCKED_TOAST_THROTTLE_MS) {
    return;
  }
  lastBlockedToastAt = now;

  // Отдельная подсказка для второго пайка одному отряду: обычный текст задачи
  // тут не объясняет, почему клик «не сработал». «Покорми остальных» — только
  // если голодные отряды правда есть (в главе 1 отряд один). Прочие
  // заблокированные пайки (не тот отряд, не та ферма) — текстом задания ниже
  if (action?.type === 'DISTRIBUTE_SUPPLY' && checkIfActionGivesAdditionalSupply(action, store.getState())) {
    const gameState = store.getState().shared.gameState;
    const currentPlayer = gameState.players?.[gameState.currentPlayerIndex];
    const suppliedUnitIds = new Set((gameState.pendingSupply || []).map((supply) => supply.unitId));
    const hasUnsuppliedUnits = (gameState.units || [])
      .some((unit) => unit.ownerId === currentPlayer?.id && !suppliedUnitIds.has(unit.id));
    store.dispatch(showToast(
      hasUnsuppliedUnits
        ? 'Этому отряду паёк уже отдан. Дополнительное снабжение сейчас не нужно — покорми остальных.'
        : 'Этому отряду паёк уже отдан. Второй паёк сейчас не нужен.',
      'info',
      4000,
      true
    ));
    REGISTRY_SINGLETON.getSoundManager()?.playToastError?.();
    return;
  }

  const taskText = step?.task?.hint || step?.task?.text || 'следуй указаниям обучения';
  // Тост остается info (noSound), но звучит как ошибка: сигнал «действие не
  // прошло» должен отличаться от обычных подсказок. Опциональная цепочка —
  // страховка для headless-прогонов без звуковой системы
  store.dispatch(showToast(`Сейчас нужно: ${taskText}`, 'info', 4000, true));
  REGISTRY_SINGLETON.getSoundManager()?.playToastError?.();
};

// Модалка шага показывается с задержкой: игрок должен успеть увидеть
// результат своего действия (анимации построек, доходов, путей влияния).
// Переопределяется шагом через step.modalDelayMs (например, дольше для
// шагов после стройки гильдии — у ее анимации свой таймаут + длительность)
const DEFAULT_STEP_MODAL_DELAY_MS = 1000;

// Модалка шага не накрывает удары ближнего боя. Сколько они идут, шаг заранее
// не знает (зависит от числа отрядов и целей), поэтому модалка ждет их конца
// по факту (см. utils/effects/meleeAnimationTracker) и еще столько — итог боя
// на плашках отрядов должен побыть на виду
export const STEP_MODAL_AFTER_MELEE_MS = 600;
// Страховка: вкладку могли скрыть посреди ударов (кадры не идут, анимация
// стоит) — модалка не ждет их вечно
export const STEP_MODAL_MELEE_WAIT_LIMIT_MS = 5000;

/** Показать модалку шага, когда отыграют удары ближнего боя и итог побудет на поле */
export const showAfterMeleeStrikes = (show) => {
  if (!checkIfAnyMeleeResultHeld()) {
    const lastReleaseAt = getLastMeleeResultReleaseAt();
    const sinceReleaseMs = lastReleaseAt === null ? Infinity : Date.now() - lastReleaseAt;
    if (sinceReleaseMs >= 0 && sinceReleaseMs < STEP_MODAL_AFTER_MELEE_MS) {
      setTimeout(show, STEP_MODAL_AFTER_MELEE_MS - sinceReleaseMs);
    } else {
      show();
    }
    return;
  }

  let unsubscribe = () => {};
  // Зовется один раз: и подписка, и страховочный таймер тут же снимаются
  const settle = (delayMs) => {
    unsubscribe();
    clearTimeout(limitTimer);
    if (delayMs > 0) {
      setTimeout(show, delayMs);
    } else {
      show();
    }
  };
  const limitTimer = setTimeout(() => settle(0), STEP_MODAL_MELEE_WAIT_LIMIT_MS);
  unsubscribe = subscribeToMeleeResultHolds(() => {
    if (!checkIfAnyMeleeResultHeld()) {
      settle(STEP_MODAL_AFTER_MELEE_MS);
    }
  });
};

const applyStepEntryEffects = (store, step, stepIndex, {isModalChain = false} = {}) => {
  if (!step) {
    return;
  }
  if (step.dice?.damage) {
    enqueueTutorialDamageDice(step.dice.damage);
  }
  // Шаг требует РУЧНОГО выбора отряда (например, докупка размера после
  // найма, где нанятый отряд автовыбирается): сбрасываем выбор на входе,
  // чтобы подсветка кварталов появилась в ответ на собственный клик игрока.
  // Автовыбор при найме дополнительно подавляется в recruitNewUnitOnBarracks —
  // он диспатчится ПОСЛЕ продвижения шага и иначе перезаписал бы этот сброс
  if (step.deselectsUnitOnEntry) {
    store.dispatch(deselectUnit());
  }
  if (!step.modalId) {
    return;
  }

  // Цепочка «модалка за модалкой» (шаг продвинулся закрытием предыдущей
  // модалки) — следующую показываем сразу, между ними анимаций нет.
  // Headless-прогоны (пробники) синхронны — там задержка тоже не нужна
  if (isModalChain || typeof window === 'undefined') {
    store.dispatch(tutorialSetModal(step.modalId));
    return;
  }

  const delayMs = step.modalDelayMs ?? DEFAULT_STEP_MODAL_DELAY_MS;
  const showStepModal = () => {
    // Показ только если игрок все еще на этом шаге, не открыта другая модалка
    // и модалка ЭТОГО шага еще не показывалась (isStepModalShown — страховка
    // от повторного открытия уже закрытой игроком модалки задвоившимся
    // таймером)
    const state = store.getState();
    const tutorialState = state.private?.tutorial;
    if (!tutorialState
      || tutorialState.stepIndex !== stepIndex
      || tutorialState.isChapterCompleted
      || tutorialState.activeModalId
      || tutorialState.isStepModalShown) {
      return;
    }
    store.dispatch(tutorialSetModal(step.modalId));
  };
  setTimeout(() => showAfterMeleeStrikes(showStepModal), delayMs);
};

/**
 * Продвижение сценария: после каждого экшена проверяем, выполнен ли текущий
 * шаг. Цикл — несколько шагов могут выполниться одним изменением состояния
 * (например, «дождись хода» уже истинен на входе в следующий шаг).
 */
const progressTutorial = (store, action) => {
  if (isProgressing) {
    return;
  }
  isProgressing = true;
  try {
    // Страховка от бесконечного цикла при ошибке в конфиге
    for (let guard = 0; guard < 25; guard++) {
      const state = store.getState();
      const context = getActiveTutorialContext(state);
      if (!context.chapter || context.isCompleted) {
        return;
      }

      if (!context.step) {
        store.dispatch(tutorialChapterCompleted());
        return;
      }

      if (!context.step.complete) {
        // Чисто модальный шаг: продвигается только закрытием своей модалки —
        // и только как реакция на сам экшен закрытия (не на итерации цикла)
        const tutorialState = state.private.tutorial;
        const isJustClosed = guard === 0
          && action.type === 'TUTORIAL_CLOSE_MODAL'
          && tutorialState.isStepModalShown
          && !tutorialState.activeModalId;
        if (!isJustClosed) {
          return;
        }
      } else if (!checkTutorialPredicate(state, context.step.complete)) {
        return;
      }

      store.dispatch(tutorialAdvanceStep());
      const nextContext = getActiveTutorialContext(store.getState());
      if (!nextContext.step) {
        store.dispatch(tutorialChapterCompleted());
        return;
      }
      applyStepEntryEffects(store, nextContext.step, nextContext.stepIndex, {
        isModalChain: action.type === 'TUTORIAL_CLOSE_MODAL',
      });
    }
  } finally {
    isProgressing = false;
  }
};

/**
 * Шаг с autoCompleteTurnAfterModal: фазу за игрока завершает само обучение,
 * как только он закроет модалку шага. Так сделан пропуск пустой фазы
 * Снабжения в первом раунде главы 1 — кормить там некого, и просить игрока
 * жать кнопку завершения хода незачем.
 *
 * Момент выбран не случайно: завершать ход НА ВХОДЕ в шаг нельзя (модалка
 * шага показывается с задержкой, и игрок ее просто не увидит — шаг уйдет
 * раньше по своему предикату), а после продвижения шага — поздно: гейт
 * следующего шага завершение хода уже не пропустит. Шаг при этом сохраняет
 * allow: completeTurn — и как запасной ручной путь, и чтобы headless-пробник
 * главы (tutorialChapterProbe) проходил ее как раньше
 */
const maybeAutoCompleteTurnAfterModal = (store, action) => {
  if (action.type !== 'TUTORIAL_CLOSE_MODAL') {
    return;
  }
  const state = store.getState();
  const {step} = getActiveTutorialContext(state);
  if (!step?.autoCompleteTurnAfterModal) {
    return;
  }
  // Ход бота завершает он сам (см. tutorialBotScript)
  if (selectCurrentPlayer(state)?.isBot) {
    return;
  }
  // Без звука завершения хода: ход завершает не клик игрока
  handleCompleteTurn(store, true, false);
};

/**
 * Middleware режима обучения:
 * 1. Жесткий гейт — «не те» действия игрока не применяются вовсе, вместо них
 *    info-тост с текущей задачей; этот слой страхует ВСЕ пути диспатча
 *    (поле, тулбары, каскады завершения хода).
 * 2. Двигатель сценария — детект выполнения шага по состоянию после экшена.
 */
const tutorialMiddleware = (store) => (next) => (action) => {
  // Просмотр записи прохождения: гейт, продвижение шагов и отложенные модалки
  // здесь не работают — прогресс главы целиком приходит из самой записи
  // (записанные TUTORIAL_ADVANCE_STEP/SET_MODAL/...). Иначе движок продвигал
  // бы шаги второй раз поверх записанных и начал бы отсекать действия игрока
  if (store.getState().shared?.gameState?.gameType === GAME_TYPES.REPLAY) {
    return next(action);
  }

  if (action.type === 'TUTORIAL_START_CHAPTER') {
    const result = next(action);
    const startedContext = getActiveTutorialContext(store.getState());
    // Модалка первого шага (реплика Барбы, за ней цепочкой приветствие
    // главы) — сразу: глава только загрузилась, анимаций нет
    applyStepEntryEffects(store, startedContext.step, startedContext.stepIndex, {isModalChain: true});
    return result;
  }

  const stateBefore = store.getState();
  const context = getActiveTutorialContext(stateBefore);

  if (!context.chapter) {
    return next(action);
  }

  if (!context.isCompleted && !checkTutorialActionAllowed(stateBefore, action, context.step)) {
    console.warn(`[Tutorial] blocked action ${action.type} on step ${context.step?.id}`);
    // В запись прохождения — где игрок пытался сделать «не то»
    getTutorialReplayRecorder()?.appendRecord(buildTutorialBlockedActionRecord(action, {
      stepIndex: context.stepIndex,
      stepId: context.step?.id,
      recordedAt: Date.now(),
    }));
    showBlockedActionToast(store, context.step, action);
    return action;
  }

  const result = next(action);
  progressTutorial(store, action);
  maybeAutoCompleteTurnAfterModal(store, action);
  return result;
};

export default tutorialMiddleware;
